/**
 * ISO 4217 currency data. The minor-unit exponent is data, never a constant:
 * USD and EUR are 2, KHR and JPY are 0, KWD and BHD are 3. Every amount in the
 * product is a `bigint` count of minor units, so the exponent is the only thing
 * that turns a stored integer back into a displayable figure.
 */

type CurrencyDefinition = {
  readonly code: string;
  readonly name: string;
  /** Decimal exponent: the number of digits after the decimal separator. */
  readonly minorUnit: number;
  /**
   * The ISO 4217 numeric code, kept as a three-digit string because it is an
   * identifier with leading zeros (AUD is `036`), not a quantity.
   *
   * It exists because the alphabetic code is not what every wire format wants:
   * an EMVCo merchant QR payload carries tag 53 as the numeric code
   * (`payments/merchant-qr.ts`). Two places deriving it from a lookup of their
   * own is two tables to keep current.
   */
  readonly numeric: string;
};

const DEFINITIONS = [
  { code: "AUD", name: "Australian Dollar", minorUnit: 2, numeric: "036" },
  { code: "BHD", name: "Bahraini Dinar", minorUnit: 3, numeric: "048" },
  { code: "BND", name: "Brunei Dollar", minorUnit: 2, numeric: "096" },
  { code: "CAD", name: "Canadian Dollar", minorUnit: 2, numeric: "124" },
  { code: "CHF", name: "Swiss Franc", minorUnit: 2, numeric: "756" },
  { code: "CNY", name: "Renminbi", minorUnit: 2, numeric: "156" },
  { code: "EUR", name: "Euro", minorUnit: 2, numeric: "978" },
  { code: "GBP", name: "Pound Sterling", minorUnit: 2, numeric: "826" },
  { code: "HKD", name: "Hong Kong Dollar", minorUnit: 2, numeric: "344" },
  { code: "IDR", name: "Rupiah", minorUnit: 2, numeric: "360" },
  { code: "INR", name: "Indian Rupee", minorUnit: 2, numeric: "356" },
  { code: "JOD", name: "Jordanian Dinar", minorUnit: 3, numeric: "400" },
  { code: "JPY", name: "Yen", minorUnit: 0, numeric: "392" },
  { code: "KHR", name: "Riel", minorUnit: 0, numeric: "116" },
  { code: "KRW", name: "Won", minorUnit: 0, numeric: "410" },
  { code: "KWD", name: "Kuwaiti Dinar", minorUnit: 3, numeric: "414" },
  { code: "LAK", name: "Lao Kip", minorUnit: 2, numeric: "418" },
  { code: "MMK", name: "Kyat", minorUnit: 2, numeric: "104" },
  { code: "MYR", name: "Malaysian Ringgit", minorUnit: 2, numeric: "458" },
  { code: "NZD", name: "New Zealand Dollar", minorUnit: 2, numeric: "554" },
  { code: "OMR", name: "Rial Omani", minorUnit: 3, numeric: "512" },
  { code: "PHP", name: "Philippine Peso", minorUnit: 2, numeric: "608" },
  { code: "SGD", name: "Singapore Dollar", minorUnit: 2, numeric: "702" },
  { code: "THB", name: "Baht", minorUnit: 2, numeric: "764" },
  { code: "TND", name: "Tunisian Dinar", minorUnit: 3, numeric: "788" },
  { code: "TWD", name: "New Taiwan Dollar", minorUnit: 2, numeric: "901" },
  { code: "USD", name: "US Dollar", minorUnit: 2, numeric: "840" },
  { code: "VND", name: "Dong", minorUnit: 0, numeric: "704" },
] as const satisfies readonly CurrencyDefinition[];

export type CurrencyCode = (typeof DEFINITIONS)[number]["code"];

export const CURRENCY_CODES = DEFINITIONS.map((d) => d.code) as unknown as readonly [
  CurrencyCode,
  ...CurrencyCode[],
];

const BY_CODE = new Map<string, CurrencyDefinition>(DEFINITIONS.map((d) => [d.code, d]));

export function isCurrencyCode(value: string): value is CurrencyCode {
  return BY_CODE.has(value);
}

export function currency(code: CurrencyCode): CurrencyDefinition {
  const found = BY_CODE.get(code);
  if (!found) throw new Error(`Unknown currency code: ${code}`);
  return found;
}

/**
 * The exponent for a code. Orders snapshot this onto `pos_orders.minor_unit` so a
 * later registry change cannot re-scale a settled bill.
 */
export function minorUnitOf(code: CurrencyCode): number {
  return currency(code).minorUnit;
}

export const ALL_CURRENCIES: readonly CurrencyDefinition[] = DEFINITIONS;
